import React, { useMemo, useState } from 'react'
import { localizedText } from '../utils/localized'
import { useNavigate, useParams } from 'react-router-dom'
import { FaFolderOpen, FaSearch, FaEye } from 'react-icons/fa'
import { useCurrent, useFetch } from '../hooks'
import { CardHeaderWithActions, MwSpinnerButton, NoDataFound } from '../components/ui'
import { getCatalogLabel, normalizeSupportSettings } from '../constants/ticket'
import { PROJECT_STATUSES, ProjectStatusPill, formatDate, getTimeProgress } from '../components/projects/projectView'

// مشاريع شركة العميل اللي الدعم الفني أنشأها — عرض فقط.
const ProjectsManage = () => {
    const navigate = useNavigate();
    const params = useParams();
    const { currentLangId } = useCurrent();
    const isAr = currentLangId !== 'en';
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const { data: projectsResult, loading, error } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/projects/customer`);
    const { data: supportSettingsResult } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/settings`);
    const supportSettings = normalizeSupportSettings(supportSettingsResult);
    const projects = useMemo(() => (Array.isArray(projectsResult?.data) ? projectsResult.data : []), [projectsResult]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return projects.filter((project) => {
            if (statusFilter !== 'all' && (project.status || 'running') !== statusFilter) return false;
            if (!q) return true;
            return `${project.title} ${project.projectNumber} ${project.contractNumber || ''}`.toLowerCase().includes(q);
        });
    }, [projects, search, statusFilter]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <CardHeaderWithActions title={isAr ? 'المشاريع' : 'Projects'}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, background: '#f1f5f9', color: '#475569', fontSize: 11, fontWeight: 800 }}>
                    <FaEye size={11} />
                    {isAr ? 'عرض فقط' : 'View only'}
                </span>
            </CardHeaderWithActions>

            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: '1 1 260px' }}>
                    <FaSearch size={12} style={{ position: 'absolute', top: '50%', insetInlineStart: 12, transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    <input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder={isAr ? 'بحث باسم المشروع أو رقمه أو رقم العقد...' : 'Search by project name, number or contract...'}
                        style={{ width: '100%', height: 40, borderRadius: 10, border: '1px solid #cbd5e1', paddingInlineStart: 32, paddingInlineEnd: 12, fontSize: 13 }}
                    />
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {[{ value: 'all', ar: 'الكل', en: 'All' }, ...PROJECT_STATUSES].map((item) => {
                        const active = statusFilter === item.value;
                        const count = item.value === 'all' ? projects.length : projects.filter((project) => (project.status || 'running') === item.value).length;
                        return (
                            <button
                                key={item.value}
                                type="button"
                                onClick={() => setStatusFilter(item.value)}
                                style={{ padding: '6px 12px', borderRadius: 999, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 800, background: active ? '#0f172a' : '#f1f5f9', color: active ? '#fff' : '#475569' }}
                            >
                                {isAr ? item.ar : item.en} ({count})
                            </button>
                        );
                    })}
                </div>
            </div>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><MwSpinnerButton withLabel={false} isFullCenter /></div>
            ) : error ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#b91c1c', fontWeight: 700, fontSize: 13 }}>{isAr ? 'تعذر تحميل المشاريع' : 'Failed to load projects'}</div>
            ) : !filtered.length ? (
                <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 32 }}>
                    <NoDataFound msg={projects.length ? (isAr ? 'لا توجد مشاريع مطابقة' : 'No matching projects') : (isAr ? 'لا توجد مشاريع لشركتك حتى الآن' : 'Your company has no projects yet')} />
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
                    {filtered.map((project) => {
                        const progress = getTimeProgress(project);
                        const section = project.sectionId ? getCatalogLabel(supportSettings.sections, project.sectionId, currentLangId) : '';
                        return (
                            <button
                                key={project._id}
                                type="button"
                                onClick={() => navigate(`/${params.tenant}/projects/show/${project._id}`)}
                                style={{ textAlign: 'start', background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 16, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 1px 3px rgba(15,23,42,0.04)' }}
                                onMouseEnter={(event) => { event.currentTarget.style.borderColor = '#a5b4fc'; }}
                                onMouseLeave={(event) => { event.currentTarget.style.borderColor = '#e2e8f0'; }}
                            >
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                    <span style={{ width: 38, height: 38, borderRadius: 10, background: '#eef2ff', color: '#4f46e5', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <FaFolderOpen size={16} />
                                    </span>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 11, fontWeight: 800, color: '#4f46e5' }}>
                                            <span>{isAr ? 'مشروع' : 'Project'} #{project.projectNumber ?? '-'}</span>
                                            <ProjectStatusPill status={project.status} lang={currentLangId} />
                                        </div>
                                        <div style={{ fontSize: 15, fontWeight: 900, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={localizedText(project, 'title', currentLangId)} dir="auto">{localizedText(project, 'title', currentLangId)}</div>
                                        {section && <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{section}</div>}
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, fontSize: 11 }}>
                                    <div><div style={{ color: '#94a3b8', fontWeight: 800 }}>{isAr ? 'البداية' : 'Start'}</div><div style={{ color: '#0f172a', fontWeight: 700 }}>{formatDate(project.startAt)}</div></div>
                                    <div><div style={{ color: '#94a3b8', fontWeight: 800 }}>{isAr ? 'النهاية المتوقعة' : 'Expected end'}</div><div style={{ color: '#0f172a', fontWeight: 700 }}>{formatDate(project.expectedAt)}</div></div>
                                    <div><div style={{ color: '#94a3b8', fontWeight: 800 }}>{isAr ? 'التذاكر' : 'Tickets'}</div><div style={{ color: '#0f172a', fontWeight: 700 }}>{project.ticketsCount || 0}</div></div>
                                </div>
                                {progress !== null && (
                                    <div>
                                        <div style={{ height: 6, borderRadius: 999, background: '#f1f5f9', overflow: 'hidden' }}>
                                            <div style={{ width: `${progress}%`, height: '100%', background: progress >= 100 ? '#f59e0b' : '#6366f1' }} />
                                        </div>
                                        <div style={{ marginTop: 4, fontSize: 10, color: '#94a3b8', fontWeight: 700 }}>{isAr ? `مر ${progress}% من مدة المشروع` : `${progress}% of the project time elapsed`}</div>
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ProjectsManage;
